import React, { useState, useRef, useEffect } from "react";
import { Box, Typography, Grid, Button, Container, TextField, CircularProgress, SvgIcon, Tooltip, Drawer, IconButton, Divider } from "@mui/material";
import {
  Diversity3 as ConnectIcon,
  AutoAwesome as InnovateIcon,
  RocketLaunch as SucceedIcon,
  Public as GlobalIcon,
  Handshake as CollabIcon,
  Share as ShareIcon,
  TrendingUp as ImpactIcon,
  ArrowForward as ArrowIcon,
  Menu as MenuIcon,
  Close as CloseIcon,
  Lock as LockIcon,
} from "@mui/icons-material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useUserActions } from "shared/hooks/user.actions";
import { getOAuthUrl, OAUTH_PROVIDERS } from "../config/oauth";
import LoginForm from "../forms/LoginForm";
import RegistrationForm from "../forms/RegistrationForm";
import logo from "media/otable_main.png";
import logoo from "media/otable_logoo.png";
import logo_alt from "media/otable_alt.png";
import collaborationImage from "media/round_table.jpg";

const GoogleIcon = (props) => (
  <SvgIcon {...props} viewBox="0 0 24 24">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </SvgIcon>
);

const HERO_GRADIENT = "linear-gradient(135deg, rgba(102,126,234,0.85), #764ba2d9)";
const DEEP_PURPLE   = "#1A0535";
const BG_NAVBAR   = "linear-gradient(90deg, #120328 0%, #2D1060 50%, #1A0535 100%)";
const MID_PURPLE    = "#5B3FA0";
const SOFT_LAVENDER = "#F3F0FF";

const NAV_H = 56; // px — slightly taller to fit the inputs

// Shared sx for both nav inputs
const navInputSx = {
  "& .MuiOutlinedInput-root": {
    height: 34,
    color: "#595959",
    fontSize: "1.25rem",
    borderRadius: "6px",
    backgroundColor: "#ffffff",
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(255, 255, 255, 0.23)",
      top: 0, // Reset the top position
      "& legend": { 
        display: "none",
        height: 0,
        width: 0,
        padding: 0
      }
    },
    "&:hover .MuiOutlinedInput-notchedOutline": { 
      borderColor: "rgba(0,0,0,0.45)" 
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { 
      borderColor: "#595959", 
      borderWidth: 1,
      top: 0, // Reset the top position
      "& legend": { 
        display: "none",
        height: 0,
        width: 0,
        padding: 0
      }
    },
    "& input": { py: 0, px: 1.25 },
    "& input::placeholder": { color: "#999999", opacity: 1 },
    "&.Mui-error input::placeholder": { color: "#d32f2f", opacity: 1 },
    "& input:-webkit-autofill": {
      WebkitBoxShadow: "0 0 0 100px #ffffff inset",
      WebkitTextFillColor: "#595959",
    },
  },
};


export default function LandingPage() {
  // ?signup=1&email=… (invitation emails) opens the sign-up form with the invited address filled in
  const [searchParams] = useSearchParams();
  const invitedEmail = (searchParams.get("email") || "").trim().slice(0, 254);
  const [mode, setMode] = useState(() => (searchParams.get("signup") || invitedEmail ? "register" : "login"));
  const [navEmail, setNavEmail]       = useState("");
  const [navPassword, setNavPassword] = useState("");
  const [navLoading, setNavLoading]   = useState(false);
  const [navError, setNavError]       = useState("");
  const [navVisible, setNavVisible]   = useState(false);
  const [fundingCalls, setFundingCalls] = useState([]);

  const navigate     = useNavigate();
  const userActions  = useUserActions();

  const heroRef     = useRef(null);
  const aboutRef    = useRef(null);
  const featuresRef = useRef(null);
  const fundingRef  = useRef(null);

  useEffect(() => {
    const base = process.env.REACT_APP_API_BASE_URL || "";
    fetch(`${base}/funding/calls/public-upcoming/`)
      .then((r) => r.ok ? r.json() : null)
      .then((data) => { if (data) setFundingCalls(data.results ?? data); })
      .catch(() => {});
  }, []);

  // Show navbar only after the hero scrolls out of view
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setNavVisible(!entry.isIntersecting),
      { threshold: 0.05 }
    );
    const el = heroRef.current;
    if (el) observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const handleNavLogin = async () => {
    if (!navEmail || !navPassword) { setNavError("Enter email and password."); return; }
    setNavLoading(true);
    setNavError("");
    try {
      await userActions.login({ email: navEmail, password: navPassword });
      navigate("/");
    } catch {
      setNavError("Invalid credentials.");
    } finally {
      setNavLoading(false);
    }
  };

  const onNavKey = (e) => { if (e.key === "Enter") handleNavLogin(); };

  const scrollTo = (ref) =>
    ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <Box sx={{ overflowX: "hidden" }}>

      {/* ─── STICKY NAVBAR ─── */}
      <Box
        component="nav"
        sx={{
          position: "fixed", top: 0, left: 0, right: 0, zIndex: 400,
          height: NAV_H,
          display: "flex", alignItems: "center",
          px: { xs: 2, md: 4 },
          gap: { md: 1.5 },
          background: BG_NAVBAR,
          backdropFilter: "blur(14px)",
          borderBottom: "1px solid rgba(255,255,255,0.07)",
          transform: navVisible ? "translateY(0)" : "translateY(-100%)",
          transition: "transform 0.3s cubic-bezier(0.4,0,0.2,1)",
        }}
      >
        {/* Logo */}
        <img src={logo} alt="Otable" style={{ 
                height: 35, 
                // cursor: "pointer", 
                // flexShrink: 0,
              
                display: { xs: 'inline-flex', md: 'none' },
                // position: 'absolute',
                top: '50%',
                left: '49%',
                // transform: 'translate(-50%, -50%)',
                color: 'white',
                // bgcolor: 'rgba(0,0,0,0.32)',
                p: '3px',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.52)' },
              
              
         
              
              }}
          onClick={() => scrollTo(heroRef)} />


        {/* Section links — hidden on small screens */}
        <Box sx={{ display: { lg: "flex" }, gap: 0.5, mr: 1.5,  ml: 2 }}>
          {[
            { label: "About",    fn: () => scrollTo(aboutRef)    },
            { label: "Features", fn: () => scrollTo(featuresRef) },
            { label: "Funding", fn: () => scrollTo(fundingRef) },
            { label: "Blog",     fn: () => navigate("/blog/")    },
          ].map(({ label, fn }) => (
            <Button key={label} onClick={fn} disableRipple
              sx={{
                color: "rgba(255,255,255,0.8)", textTransform: "none",
                fontWeight: 500, fontSize: "1.4rem",
                "&:hover": { color: "#fff", bgcolor: "transparent" },
              }}
            >
              {label}
            </Button>
          ))}
        </Box>


        <Box sx={{ flex: 1 }} />


        {/* ── Inline login form — hidden on small screens ── */}
        <Box
          sx={{
            display: { xs: "none", md: "flex" },
            alignItems: "center", gap: 1,
          }}
        >
          <TextField
            placeholder={navError ? "Email *" : "Email"}
            type="email"
            size="small"
            value={navEmail}
            error={!!navError}
            onChange={(e) => { setNavEmail(e.target.value); setNavError(""); }}
            onKeyDown={onNavKey}
            disabled={navLoading}
            sx={{ width: 180, ...navInputSx }}
          />
          <TextField
            placeholder={navError ? "Password *" : "Password"}
            type="password"
            size="small"
            value={navPassword}
            error={!!navError}
            onChange={(e) => { setNavPassword(e.target.value); setNavError(""); }}
            onKeyDown={onNavKey}
            disabled={navLoading}
            sx={{ width: 180, ...navInputSx }}
          />
          <Button
            onClick={handleNavLogin}
            disabled={navLoading}
            disableElevation
            sx={{
              height: 34,
              minWidth: 34,
              px: 1.25,
              bgcolor: "rgba(102,126,234,0.85)",
              color: "#fff",
              borderRadius: "6px",
              textTransform: "none",
              fontWeight: 600,
              fontSize: "1.3rem",
              flexShrink: 0,
              "&:hover": { bgcolor: "rgba(118,75,162,0.9)" },
              "&:disabled": { bgcolor: "rgba(102,126,234,0.4)", color: "rgba(255,255,255,0.5)" },
            }}
          >
            {navLoading
              ? <CircularProgress size={14} sx={{ color: "#fff" }} />
              : <Typography sx={{ fontSize: "1.3rem", color: "#fff" }}>Sign in</Typography>
            }
          </Button>

          {OAUTH_PROVIDERS.google.clientId && (
            <Tooltip title="Sign in with Google" arrow>
              <Button
                onClick={() => { window.location.href = getOAuthUrl("google"); }}
                disabled={navLoading}
                disableElevation
                sx={{
                  height: 34, minWidth: 34, px: 1,
                  bgcolor: "#fff",
                  borderRadius: "6px",
                  flexShrink: 0,
                  "&:hover": { bgcolor: "#f1f1f1" },
                  "&:disabled": { bgcolor: "rgba(255,255,255,0.3)" },
                }}
              >
                <GoogleIcon sx={{ fontSize: 20 }} />
              </Button>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* ─── ORIGINAL HERO (unchanged) ─── */}
      <Box
        ref={heroRef}
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          background: {
            xs: "linear-gradient(90deg, rgba(245,247,255,1), #FFFFFF 40%)",
            sm: "linear-gradient(90deg, rgba(245,247,255,1), #FFFFFF 40%)",
          },
        }}
      >
        <Grid
          container
          sx={{ flex: 1, flexDirection: { xs: "column-reverse", sm: "column-reverse", md: "row" } }}
        >
          {/* Left — image + motto */}
          <Grid item xs={12} md={6}
            sx={{
              position: "relative",
              display: "flex", flexDirection: "column",
              alignItems: "center", justifyContent: "center",
              background: `${HERO_GRADIENT}, url(${collaborationImage})`,
              backgroundSize: "cover", backgroundPosition: "center",
              p: 4, overflow: "hidden",
            }}
          >

<Box sx={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.2)", zIndex: 0 }} />

{/* About / Features links — top-right of the white panel */}
<Box
      sx={{
        position: "absolute", top: 20, left: 34,
        display: { xs: "none", md: "flex" },
        alignItems: "center", gap: 0.5,
      }}
    >
      <img src={logo} alt="Otable" style={{ 
                      height: 35, 
                      // cursor: "pointer", 
                      // flexShrink: 0,
                    
                      display: { xs: 'inline-flex', md: 'none' },
                      // position: 'absolute',
                      // top: '50%',
                      // left: '49%',
                      // transform: 'translate(-50%, -50%)',
                      color: 'white',
                      // bgcolor: 'rgba(0,0,0,0.32)',
                      // p: '3px',
                      '&:hover': { bgcolor: 'rgba(0,0,0,0.52)' },
                    
                    }}
                onClick={() => scrollTo(heroRef)} />

<Box sx={{ ml: 1.5, mb: -0.25 }}>   

              {[
                { label: "About",    fn: () => scrollTo(aboutRef)    },
                { label: "Features", fn: () => scrollTo(featuresRef) },
                { label: "Funding", fn: () => scrollTo(fundingRef) },
                { label: "Blog",     fn: () => navigate("/blog/")    },
              ].map(({ label, fn }) => (
                <Button key={label} onClick={fn} disableRipple
                  sx={{
                    color: "rgb(238, 238, 238)",
                    textTransform: "none", fontWeight: 500, fontSize: "1.4rem",
                    letterSpacing: 0.2, borderRadius: 2.5, ml: 1,
                    // bgcolor: "rgba(253, 253, 253, 0.05)" ,

                    "&:hover": { color: "#fff",
                      // fontWeight: 550,
                      bgcolor: "rgba(253, 253, 253, 0.05)" },

                  }}
                >
                  {label}
                </Button>
              ))}
            </Box>
            </Box>


            {/* Connect / Innovate / Succeed footer strip */}
            <Box
              sx={{
                position: "absolute", bottom: 0, left: 0, width: "100%", py: 2.5,
                display: "flex", flexDirection: "column", gap: 1,
                justifyContent: "center", alignItems: "center",
                background: "rgba(0,0,0,0.18)", backdropFilter: "blur(4px)", zIndex: 2,
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "row", gap: 1, justifyContent: "center", alignItems: "center" }}>
                {[
                  { icon: <ConnectIcon  sx={{ fontSize: "1.6rem", color: "rgba(255,255,255,0.95)" }} />, text: "Connect"  },
                  { icon: <InnovateIcon sx={{ fontSize: "1.6rem", color: "rgba(255,255,255,0.95)" }} />, text: "Innovate" },
                  { icon: <SucceedIcon  sx={{ fontSize: "1.6rem", color: "rgba(255,255,255,0.95)" }} />, text: "Succeed"  },
                ].map((item, idx) => (
                  <Box key={idx} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    {item.icon}
                    <Typography variant="body2"
                      sx={{ color: "white", fontWeight: 550, textTransform: "uppercase", letterSpacing: "1.5px", fontSize: "1.1rem" }}>
                      {item.text}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>

            {/* Headline + feature cards */}
            <Box sx={{ position: "relative", zIndex: 1, textAlign: "center", maxWidth: "600px", pb: { xs: 4, sm: 4, md: 0 } }}>
              <Box component="a" href="/blog/research-privacy/"
                onClick={(e) => { e.preventDefault(); navigate("/blog/research-privacy/"); }}
                sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, mb: 3, px: 1.75, py: 0.6,
                  borderRadius: 999, textDecoration: "none", color: "#fff",
                  bgcolor: "rgba(255,255,255,0.14)", border: "1px solid rgba(255,255,255,0.3)", backdropFilter: "blur(6px)",
                  fontSize: "1.25rem", fontWeight: 600, transition: "background .2s",
                  "&:hover": { bgcolor: "rgba(255,255,255,0.24)" } }}>
                <LockIcon sx={{ fontSize: "1.5rem" }} />
                Your unpublished research stays yours
                <ArrowIcon sx={{ fontSize: "1.4rem", opacity: 0.8 }} />
              </Box>
              <Typography variant="h2"
                sx={{ color: "white", fontWeight: 700, mb: 2, textShadow: "0 2px 4px rgba(0,0,0,0.2)",
                  fontSize: { xs: "2.5rem", sm: "3rem", md: "3rem", lg: "3rem" } }}>
                Build Your Consortium Today!
                {/*{" "}  <img src={logo} alt="Otable Logo" style={{ height: 60, marginBottom: 5, marginRight: 10, marginLeft: 10 }} /> */}
              
              </Typography>

              <Typography variant="h4"
                sx={{ color: "rgba(255,255,255,0.95)", fontWeight: 400, mb: 2, lineHeight: 1.55,
                  textShadow: "0 1px 2px rgba(0,0,0,0.1)", fontSize: "1.55rem",
                  display: { xs: "none", sm: "none", md: "block" } }}>
                Where Researchers Unite, Ideas Flourish
              </Typography>

              <Box sx={{ maxWidth: "520px", margin: "0 auto", marginTop: 7, gap: 2,
                gridTemplateColumns: { xs: "1fr 1fr", sm: "1fr 1fr" },
                display: { xs: "none", sm: "none", md: "grid" } }}>
                {[
                  { icon: <GlobalIcon  sx={{ fontSize: "2.5rem" }} />, text: "Global network of innovators"    },
                  { icon: <CollabIcon  sx={{ fontSize: "2.5rem" }} />, text: "Groundbreaking collaborations"   },
                  { icon: <ShareIcon   sx={{ fontSize: "2.5rem" }} />, text: "Interdisciplinary expertise"     },
                  { icon: <ImpactIcon  sx={{ fontSize: "2.5rem" }} />, text: "Research into real impact"       },
                ].map((item, idx) => (
                  <Box key={idx}
                    sx={{
                      display: "flex", flexDirection: "column", alignItems: "center",
                      textAlign: "center", gap: 1,
                      bgcolor: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.12)",
                      backdropFilter: "blur(8px)", borderRadius: 3, px: 2, py: 2.5,
                      transition: "all 0.3s ease",
                      "&:hover": { bgcolor: "rgba(255,255,255,0.15)", borderColor: "rgba(255,255,255,0.25)",
                        transform: "translateY(-3px)", boxShadow: "0 8px 24px rgba(0,0,0,0.15)" },
                    }}
                  >
                    <Box sx={{ width: 44, height: 44, borderRadius: "50%", bgcolor: "rgba(255,255,255,0.12)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      color: "rgba(255,255,255,0.95)", mb: 0.5 }}>
                      {item.icon}
                    </Box>
                    <Typography variant="body2"
                      sx={{ color: "rgba(255,255,255,0.9)", fontWeight: 500,
                        fontSize: { xs: "1.1rem", sm: "1.2em", md: "1.4rem" }, lineHeight: 1.4 }}>
                      {item.text}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Grid>

          {/* Right — login / register form */}
          <Grid item xs={12} md={6}
            sx={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "center",
              p: { xs: 4, sm: 4 },
              background: "linear-gradient(90deg, rgba(245,247,255,0.9), #ffffff 40%)" }}>

          


            <Box sx={{ maxWidth: "450px", width: "100%", textAlign: "center", py: 5 }}>
              <Typography variant="h4" fontWeight="bold" mb={1} color="text.primary">
                <img src={logo_alt} alt="Otable Logo" style={{ height: 60, marginBottom: 5 }} />
              </Typography>
              <Typography variant="body1" color="text.secondary" fontSize={14} mb={4}>
                {mode === "login" ? "Continue Your Collaboration Journey" : "Join a Consortium Today"}
              </Typography>
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                {mode === "login"
                  ? <LoginForm onSwitchToRegister={() => setMode("register")} />
                  : <RegistrationForm onSwitchToLogin={() => setMode("login")} initialEmail={invitedEmail} />}
              </Box>
              <Box sx={{ mt: 4, mx: "auto", maxWidth: 380, display: "flex", alignItems: "flex-start", gap: 1.25, textAlign: "left",
                p: 1.5, borderRadius: 2, border: "1px solid rgba(90,63,160,0.18)", bgcolor: "rgba(102,126,234,0.05)" }}>
                <LockIcon sx={{ fontSize: "1.8rem", color: "#5A3FA0", mt: 0.25 }} />
                <Typography sx={{ fontSize: "1.25rem", color: "text.secondary", lineHeight: 1.55 }}>
                  <Box component="span" sx={{ fontWeight: 700, color: "text.primary" }}>Private by design.</Box>{" "}
                  Your project document drafts are private to your team and stored encrypted; Otable staff cannot open them.<br />
                  <Box component="a" href="/blog/research-privacy/"
                    onClick={(e) => { e.preventDefault(); navigate("/blog/research-privacy/"); }}
                    sx={{ color: "#5A3FA0", fontWeight: 600 }}>How it works</Box>
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* ─── SECTION 1 — ABOUT ─── */}
      {/* Deep purple: maximum contrast against white text */}
      <Box
        ref={aboutRef}
        sx={{ bgcolor: DEEP_PURPLE, py: { xs: 10, md: 18 }, px: {xs: 5, sm: 10, md: 15}}}
      >
        <Container maxWidth="lg" >
          <Grid container spacing={{ xs: 6, md: 10}} alignItems="center">

            {/* Big headline */}
            <Grid item xs={12} md={6} sx={{ display: "flex", flexDirection: "column", 
                                          alignItems: { xs: "center", md: "flex-start" }, 
                                          textAlign: { xs: "center", md: "left" },
                                           }}>
              
              
              <Typography
                component="h2"
                sx={{
                  fontFamily: 'Georgia, "Times New Roman", serif',
                  fontWeight: 700,
                  fontSize: { xs: "2.8rem", sm: "3.5rem", md: "4.5rem", lg: "5.5rem" },
                  lineHeight: 1.5,
                  color: "#fff",
                  letterSpacing: "-1px",
                  
                  
                }}
              >
                Research<br /> collaboration, <br />
                <Box component="span"
                  sx={{ background: HERO_GRADIENT, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", 
                    fontSize: { xs: "2.8rem", sm: "3.5rem", md: "4.5rem", lg: "5.5rem" }, 
                    fontWeight: 700, letterSpacing: "-1px", }}>
                  redesigned
                </Box>
               
              </Typography>
              
            
            </Grid>

            {/* Body text + stats */}
            <Grid item xs={12} md={6}>
     

            <Typography
  sx={{
    fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2rem", lg: "2.25rem" },
    fontWeight: 500,
    color: "rgba(255,255,255,0.75)",
    lineHeight: {xs: 1.8, sm: 2, md: 2.2},
    mb: 6,
  }}
>
  {/* <img 
    src={logo} 
    alt="Otable" 
    style={{ 
      height: 50, 
      marginLeft: '-5px',
      marginBottom: '5px',  // Add bottom margin
      marginRight: '15px',   // Add right margin
      opacity: 0.85
    }}
  /> */}

<Box component="span" sx={{
                display: "inline-flex", alignItems: "center", verticalAlign: "middle",
                height: { xs: 30, sm: 40, md: 50, lg: 60 },
                mr: { xs: 1, sm: 1.5, md: 2 },
                ml: { xs: -0.5, sm: -0.75, md: -1, lg: -1.5 },
                mb: { xs: 0.6, sm: 0.7, md: 0.85, lg: 1 },
              }}>
                <img
                  src={logo}
                  alt="Otable"
                  style={{ height: "100%", width: "auto", display: "block", opacity: 0.9, marginBottom: 0 }}
                />
              </Box>

is a research collaboration platform built for the modern academic — connecting researchers across institutions, enabling structured consortium formation, and providing a unified workspace for the full grant lifecycle from call discovery to proposal submission.

</Typography>


              {/* Stats row */}
              <Box sx={{ display: "flex", gap: { xs: 4, md: 6 }, flexWrap: "wrap" }}>
                {[
                  // { value: "1000+",   label: "Researchers"   },
                  { value: "200+", label: "Funding Calls" },
                  { value: "50+",   label: "Consortia" },
                ].map(({ value, label }) => (
                  <Box key={label}>
                    <Typography
                      sx={{
                        fontWeight: 800,
                        fontSize: { xs: "2.8rem", md: "3.8rem" },
                        color: "#fff",
                        lineHeight: 1,
                        fontVariantNumeric: "tabular-nums",
                        letterSpacing: "-1.5px",
                      }}
                    >
                      {value}
                    </Typography>
                    <Typography
                      sx={{
                        mt: 0.5,
                        fontSize: { xs: ".8rem", sm:"1rem", md: "1.2rem", lg: "1.3rem" },
                        textTransform: "uppercase",
                        letterSpacing: "1.5px",
                        color: "rgba(255,255,255,0.6)",
                      }}
                    >
                      {label}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Grid>
          </Grid>

          {/* ─── Need cards ─── */}
          <Box sx={{ mt: { xs: 10, md: 14 }, display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" }, gap: 3 }}>
            {[
              {
                eyebrow: "The Problem",
                icon: (
                  <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                    <circle cx="14" cy="14" r="11" stroke="url(#np1)" strokeWidth="2"/>
                    <path d="M14 9v5.5M14 17.5v1" stroke="url(#np1)" strokeWidth="2.2" strokeLinecap="round"/>
                    <defs><linearGradient id="np1" x1="3" y1="3" x2="25" y2="25"><stop stopColor="#667eea"/><stop offset="1" stopColor="#764ba2"/></linearGradient></defs>
                  </svg>
                ),
                title: "Collaboration is fragmented",
                body: "Academic collaboration today relies on personal networks and email chains. Funding opportunities are scattered across dozens of agency websites, consortium formation is unstructured, and proposal writing lacks a shared workspace — slowing down science that could move faster.",
              },
              {
                eyebrow: "Who It's For",
                icon: (
                  <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                    <circle cx="11" cy="10" r="4" stroke="url(#np2)" strokeWidth="2"/>
                    <path d="M3.5 23c0-4.5 3.4-7 7.5-7s7.5 2.5 7.5 7" stroke="url(#np2)" strokeWidth="2" strokeLinecap="round"/>
                    <circle cx="20" cy="10" r="3" stroke="url(#np2)" strokeWidth="1.8"/>
                    <path d="M20 15.5c2.8.4 4.5 2.3 4.5 5.5" stroke="url(#np2)" strokeWidth="1.8" strokeLinecap="round"/>
                    <defs><linearGradient id="np2" x1="3" y1="3" x2="25" y2="25"><stop stopColor="#667eea"/><stop offset="1" stopColor="#764ba2"/></linearGradient></defs>
                  </svg>
                ),
                title: "Built for every researcher",
                body: "From early-career scientists joining their first consortium to senior PIs coordinating multi-partner European projects. Research offices and technology transfer units use Otable to support their institution's collaboration strategy and track portfolio-level funding activity.",
              },
              {
                eyebrow: "Why Otable",
                icon: (
                  <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                    <path d="M14 3.5 17 10l7 1-5 5 1.2 7L14 20l-6.2 3.2L9 16.5 4 11l7-1z" stroke="url(#np3)" strokeWidth="2" strokeLinejoin="round"/>
                    <defs><linearGradient id="np3" x1="4" y1="4" x2="24" y2="24"><stop stopColor="#667eea"/><stop offset="1" stopColor="#764ba2"/></linearGradient></defs>
                  </svg>
                ),
                title: "Purpose-built, time-saving",
                body: "Unlike generic project management tools or academic social networks, Otable speaks the language of research funding programs, cooperation types, eligibility criteria, proposal structures. Built for the sole purpose of funding original research through bringing right people together at the right time.",
              },
            ].map(({ eyebrow, icon, title, body }) => (
              <Box key={title} sx={{
                p: { xs: 3, md: 3.5 },
                // borderRadius: "20px",
                position: "relative", overflow: "hidden",
                // border: "1px solid rgba(255,255,255,0.10)",
                background: "linear-gradient(150deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.16) 50%,  rgba(102,126,234,0.22) 100%)",
                backdropFilter: "blur(16px)",
                boxShadow: "0 4px 32px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.12)",
                display: "flex", flexDirection: "column", gap: 2,
                transition: "transform 0.22s ease, box-shadow 0.22s ease",
                "&:hover": {
                  transform: "translateY(-4px)",
                  // boxShadow: "0 14px 44px rgba(102,126,234,0.22), inset 0 1px 0 rgba(255,255,255,0.16)",
                },
                "&::before": {
                  content: '""', position: "absolute", top: 0, left: 0, right: 0, height: "45%",
                  background: "linear-gradient(180deg, rgba(255,255,255,0.07) 0%, transparent 100%)",
                   pointerEvents: "none",
                  //  borderRadius: "20px 20px 0 0",
                },
              }}>
                {/* Eyebrow + icon */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  {icon}
                  <Typography sx={{
                    fontSize: "1.3rem", fontWeight: 800, letterSpacing: "1.4px",
                    textTransform: "uppercase", color: "rgba(187, 162, 244, 0.85)",
                  }}>
                    {eyebrow}
                  </Typography>
                </Box>

                {/* Title */}
                <Typography sx={{
                  fontSize: { xs: "2.35rem", md: "2.65rem" }, fontWeight: 700,
                  color: "#fff", lineHeight: 1.3, letterSpacing: "-0.3px",
                }}>
                  {title}
                </Typography>

                {/* Divider */}
                <Box sx={{ height: "1px", bgcolor: "rgba(255,255,255,0.07)", mx: -0.5 }} />

                {/* Body */}
                <Typography sx={{
                  fontSize: { xs: "1.25rem", md: "1.45rem" },
                  color: "rgba(255,255,255,0.62)", lineHeight: 1.8,
                }}>
                  {body}
                </Typography>
              </Box>
            ))}
          </Box>

        </Container>
      </Box>

      {/* ─── SECTION 2 — FEATURES ─── */}
      {/* Light lavender: max contrast against deep purple text */}
      <Box
        ref={featuresRef}
        sx={{ bgcolor: SOFT_LAVENDER, py: { xs: 10, md: 18 } }}
      >
        <Container maxWidth="lg">

          {/* Section headline */}
          <Typography
            component="h2"
            sx={{
              fontFamily: 'Georgia,  serif',
              fontWeight: 700,
              fontSize: { xs: "2.8rem", sm: "3.5rem", md: "4rem", lg: "5rem" },
              lineHeight: 1.3,
              color: DEEP_PURPLE,
              letterSpacing: "-1px",
              mb: { xs: 4, sm: 6,md: 8 },
              maxWidth: 800,
            }}
          >
            From Idea to Impact:  <br />Built for the Researcher
          </Typography>

          {/* Feature rows */}
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            {[
              {
                label: "Connect",
                headline: "Quickly find the right people.",
                body: "Search researchers by expertise, institution, and research area. Build interdisciplinary teams across institutions and borders, then invite them into a structured consortium with one click.",
                gradient: "linear-gradient(135deg, #667EEA, #764BA2)",
              },
              {
                label: "Catch up",
                headline: "Never miss a relevant funding call.",
                body: "AI-powered matching surfaces Horizon Europe, national, and bilateral funding calls that align with your research profile and consortium capabilities. Follow funding entities and track deadlines in one place.",
                gradient: "linear-gradient(135deg, #764BA2, #4a2880)",
              },
              {
                label: "Propose",
                headline: "Write successful proposals together.",
                body: "Structured collaborative writing with work packages, task assignments, deliverables, milestones, and budgets. AI assists with section drafting, KER planning, and dissemination strategies — all export-ready.",
                gradient: "linear-gradient(135deg, #5468C8, #667EEA)",
              },
              {
                label: "Protect",
                headline: "Your unpublished research stays yours.",
                body: "Proposals and paper drafts are readable by your team only. Otable staff cannot open them, and the text is stored encrypted, so even a copy of our database or backups reveals nothing. AI features run only when a member chooses to use them, and your team sees each time text was sent to the AI provider.",
                gradient: "linear-gradient(135deg, #2E7D5B, #5468C8)",
                link: "/blog/research-privacy/",
              },
            ].map(({ label, headline, body, gradient, link }, i) => (
              <Box
                key={label}
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  alignItems: { sm: "flex-start" },
                  gap: { xs: 3, sm: 6 },
                  pt: i === 0 ? 0 : 4,
                  mt: i === 0 ? 0 : 4,
                  borderTop: i === 0 ? "none" : `1px solid rgba(90,63,160,0.15)`,
                }}
              >
                {/* Gradient label */}
                <Box sx={{ flexShrink: 0, width: { sm: 160 } }}>
                  <Typography
                    sx={{
                      fontWeight: 800,
                      fontSize: { xs: "2.3rem", sm: "2.5rem", md: "3.2rem" },
                      lineHeight: 1,
                      background: gradient,
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      letterSpacing: "-0.5px",
                    }}
                  >
                    {label}
                  </Typography>
                </Box>

                {/* Text */}
                <Box sx={{ flex: 1 }}>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: { xs: "1.5rem", md: "2rem" },
                      color: DEEP_PURPLE,
                      mb: 2,
                      lineHeight: 1.25,
                    }}
                  >
                    {headline}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: { xs: "1.25rem", md: "1.55rem" },
                      color: MID_PURPLE,
                      lineHeight: 1.8,
                      maxWidth: 640,
                    }}
                  >
                    {body}
                  </Typography>
                  {link && (
                    <Button onClick={() => navigate(link)} endIcon={<ArrowIcon />}
                      sx={{ mt: 2, px: 0, textTransform: "none", fontSize: "1.4rem", fontWeight: 600, color: DEEP_PURPLE }}>
                      How we keep your research private
                    </Button>
                  )}
                </Box>
              </Box>
            ))}
          </Box>

          {/* ─── Workflow diagram ─── */}
          <Box sx={{ mt: { xs: 10, md: 16 } }}>

            {/* Header */}
            {/* <Typography sx={{
              fontFamily: "Georgia, serif",
              fontWeight: 700,
              fontSize: { xs: "1.9rem", md: "2.6rem" },
              color: DEEP_PURPLE,
              letterSpacing: "-0.5px",
              mb: 1.5,
            }}>
              How it works
            </Typography> */}
            {/* <Typography sx={{
              fontSize: { xs: "1rem", md: "1.1rem" },
              color: MID_PURPLE,
              lineHeight: 1.75,
              maxWidth: 560,
              mb: { xs: 8, md: 11 },
            }}>
              Otable brings the entire collaboration workflow into one place — from finding the right people to submitting a winning proposal.
            </Typography> */}

            {/* Steps grid */}
            <Box sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(4, 1fr)" },
              position: "relative",
            }}>

              {/* Connector line — desktop only, sits behind the circles */}
              <Box sx={{
                display: { xs: "none", md: "block" },
                position: "absolute",
                top: "23px",
                left: "12.5%",
                right: "12.5%",
                height: "2px",
                background: "linear-gradient(90deg, #667EEA 0%, #764BA2 50%, #5468C8 100%)",
                opacity: 0.22,
                zIndex: 0,
              }} />

              {[
                {
                  n: "01", label: "Discover",
                  title: "Researcher Discovery",
                  body: "Find collaborators by expertise, research interests, and institutional affiliation across borders.",
                  icon: (
                    <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
                      <circle cx="9.5" cy="9.5" r="6" stroke="#fff" strokeWidth="1.9"/>
                      <path d="M14 14 19 19" stroke="#fff" strokeWidth="1.9" strokeLinecap="round"/>
                    </svg>
                  ),
                },
                {
                  n: "02", label: "Form",
                  title: "Consortium Formation",
                  body: "Invite partners, assign roles, manage membership, and track your consortium from first contact to signed agreement.",
                  icon: (
                    <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
                      <circle cx="11" cy="6" r="3" stroke="#fff" strokeWidth="1.9"/>
                      <circle cx="4"  cy="16" r="3" stroke="#fff" strokeWidth="1.9"/>
                      <circle cx="18" cy="16" r="3" stroke="#fff" strokeWidth="1.9"/>
                      <path d="M11 9v3m0 0-5.5 1.5M11 12l5.5 1.5" stroke="#fff" strokeWidth="1.6" strokeLinecap="round"/>
                    </svg>
                  ),
                },
                {
                  n: "03", label: "Fund",
                  title: "Funding Intelligence",
                  body: "A unified aggregator monitors calls worldwide and surfaces the ones most relevant to your profile and consortium.",
                  icon: (
                    <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
                      <path d="M11 2v2M11 18v2M2 11h2M18 11h2" stroke="#fff" strokeWidth="1.9" strokeLinecap="round"/>
                      <circle cx="11" cy="11" r="5.5" stroke="#fff" strokeWidth="1.9"/>
                      <path d="M9 11h4M11 9v4" stroke="#fff" strokeWidth="1.6" strokeLinecap="round"/>
                    </svg>
                  ),
                },
                {
                  n: "04", label: "Propose",
                  title: "Proposal Collaboration",
                  body: "Write proposals together with shared tasks, budget planning, deliverable tracking, and work-package management.",
                  icon: (
                    <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
                      <rect x="3" y="2" width="16" height="18" rx="2.5" stroke="#fff" strokeWidth="1.9"/>
                      <path d="M7 7h8M7 11h8M7 15h5" stroke="#fff" strokeWidth="1.6" strokeLinecap="round"/>
                    </svg>
                  ),
                },
              ].map(({ n, label, title, body, icon }, i) => (
                <Box key={n} sx={{
                  position: "relative", zIndex: 1,
                  display: "flex",
                  flexDirection: { xs: "row", md: "column" },
                  alignItems: { xs: "flex-start", md: "center" },
                  gap: { xs: 3, md: 0 },
                  px: { md: 2.5 },
                  pb: { xs: i < 3 ? 8 : 0, md: 0 },
                  // Mobile: vertical dashed connector between steps
                  "&::before": {
                    content: { xs: i < 3 ? '""' : "none", md: "none" },
                    position: "absolute",
                    left: "23px",
                    top: "50px",
                    bottom: 0,
                    width: "2px",
                    background: "linear-gradient(180deg, rgba(102,126,234,0.4) 0%, rgba(118,75,162,0.05) 100%)",
                  },
                }}>

                  {/* Numbered node */}
                  <Box sx={{
                    width: 48, height: 48, borderRadius: "50%",
                    flexShrink: 0,
                    background: "linear-gradient(135deg, #667EEA, #764BA2)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    boxShadow: "0 4px 20px rgba(102,126,234,0.38), 0 0 0 5px rgba(102,126,234,0.10)",
                    mb: { md: 4 },
                  }}>
                    {icon}
                  </Box>

                  {/* Text block */}
                  <Box sx={{ textAlign: { md: "center" }, flex: 1 }}>
                    <Typography sx={{
                      fontSize: "1.1rem", fontWeight: 900,
                      letterSpacing: "1.8px", textTransform: "uppercase",
                      color: "rgba(102,126,234,0.6)",
                      mb: 0.75,
                    }}>
                      {n} — {label}
                    </Typography>
                    <Typography sx={{
                      fontWeight: 700,
                      fontSize: { xs: "1.35rem", md: "1.45rem" },
                      color: DEEP_PURPLE, lineHeight: 1.45, mb: 1.25,
                    }}>
                      {title}
                    </Typography>
                    <Typography sx={{
                      fontSize: { xs: "1.4rem", md: "1.5rem" },
                      color: MID_PURPLE, lineHeight: 1.75,
                    }}>
                      {body}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>


        </Container>
      </Box>

      {/* ─── SECTION 3 — FUNDING ─── */}
      <Box ref={fundingRef} sx={{ bgcolor: "#0B0E1F", py: { xs: 10, md: 18 } }}>
        
        <Container maxWidth="lg">

          {/* Header row */}
          <Box sx={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", gap: 3, mb: { xs: 6, md: 10 } }}>
            <Typography
              component="h2"
              sx={{
                fontFamily: "'Arial Rounded MT Bold', 'Arial', sans-serif",
                fontWeight: 550,
                fontSize: { xs: "2.8rem", sm: "3.5rem", md: "4.5rem", lg: "5rem" },
                lineHeight: { xs: 1.4, sm: 1.5, md: 1.6, lg: 1.7 },
                color: "#fff",
                letterSpacing: "-0.5px",
                maxWidth: 800,
              }}
            >
              
              {/* Share Your Expertise & <br /> */}
              <Box component="span" sx={{ fontSize: { xs: "2.8rem", sm: "3.5rem", md: "4.5rem", lg: "5.5rem" }, 
                        background: HERO_GRADIENT, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                 Funding is Calling For You <br />
              </Box>

              
              <Box component="span" sx={{
                fontFamily: "'Arial Rounded MT Bold', 'Arial', sans-serif",
                fontSize: { xs: "2.8rem", sm: "3.5rem", md: "4.5rem", lg: "5.5rem" },
                lineHeight: { xs: 1.4, sm: 1.5, md: 1.6, lg: 1.7 },
                fontWeight: 550,
              }}>
                Start an
              </Box>

              <Box component="span" sx={{
                display: "inline-flex", alignItems: "center", verticalAlign: "middle",
                height: { xs: 38, sm: 48, md: 62, lg: 75 },
                mx: { xs: 1, sm: 1.5, md: 2, lg: 2.5 },
                mb: { xs: 0.4, sm: 0.7, md: 1, lg: 1.3 },
              }}>
                <img
                  src={logo}
                  alt="Otable"
                  style={{ height: "100%", width: "auto", display: "block" }}
                />
              </Box>

              <Box component="span" sx={{
                fontFamily: "'Arial Rounded MT Bold', 'Arial', sans-serif",
                fontSize: { xs: "2.8rem", sm: "3.5rem", md: "4.5rem", lg: "5.5rem" },
                lineHeight: { xs: 1.4, sm: 1.5, md: 1.6, lg: 1.7 },
                fontWeight: 550,
              }}>
                Today!
              </Box><br />

            </Typography>
            <Button
              href="/login/"
              variant="outlined"
              sx={{
                color: "rgba(255,255,255,0.75)", borderColor: "rgba(255,255,255,0.2)",
                textTransform: "none", fontWeight: 500, fontSize: "1.35rem",
                borderRadius: 2, px: 2.5, py: 1,
                "&:hover": { borderColor: "#fff", color: "#fff", bgcolor: "rgba(255,255,255,0.04)" },
              }}
            >
              Browse all calls →
            </Button>
          </Box>

          {/* Call cards */}
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr" }, 
                  gap: 3.5}}>
            {fundingCalls.map((call) => {
              const AGENCY_CAT = { NAT: "National", INT: "International", PRV: "Private", IND: "Industry", UNI: "University", VCT: "Venture Capital", NGO: "NGO" };
              const tag = call.program_cooperation_type_display || AGENCY_CAT[call.agency_category] || "";
              const deadline = call.closes_at
                ? new Date(call.closes_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
                : "Open";
              return (
              <Box
                key={call.id}
                sx={{
                  p: 3, borderRadius: "18px",
                  position: "relative", overflow: "hidden",
                  border: "1px solid rgba(255,255,255,0.14)",
                  background: "linear-gradient(145deg, rgba(255,255,255,0.11) 0%, rgba(102,126,234,0.08) 50%, rgba(118,75,162,0.13) 100%)",
                  backdropFilter: "blur(16px)",
                  boxShadow: "0 4px 24px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.18)",
                  display: "flex", flexDirection: "column", gap: 1.5,
                  transition: "transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease",
                  "&:hover": {
                    transform: "translateY(-3px)",
                    borderColor: "rgba(162,130,234,0.4)",
                    // boxShadow: "0 12px 40px rgba(102,126,234,0.28), inset 0 1px 0 rgba(255,255,255,0.22)",
                  },
                  /* gloss streak across the top half */
                  "&::before": {
                    content: '""',
                    position: "absolute", top: 0, left: 0, right: 0,
                    height: "50%",
                    background: "linear-gradient(180deg, rgba(255,255,255,0.09) 0%, rgba(255,255,255,0) 100%)",
                    borderRadius: "18px 18px 0 0",
                    pointerEvents: "none",
                  },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Typography sx={{ fontSize: "1.5rem", fontWeight: 700, textTransform: "uppercase",
                    letterSpacing: "1.2px", color: "rgba(162,130,234,0.9)" }}>
                    {tag}
                  </Typography>
                  <Typography sx={{ fontSize: ".9rem", color: "rgba(255,255,255,0.4)", fontVariantNumeric: "tabular-nums" }}>
                    {deadline}
                  </Typography>
                </Box>

                <Typography sx={{ fontSize: "1.25rem", fontWeight: 600, color: "#fff", lineHeight: 1.4, flex: 1 }}>
                  {call.title}
                </Typography>

                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pt: 1,
                  borderTop: "1px solid rgba(255,255,255,0.08)" }}>
                  <Box>
                    <Typography sx={{ fontSize: "0.9rem", color: "rgba(255,255,255,0.48)", textTransform: "uppercase", letterSpacing: "0.8px" }}>
                      {call.agency_name}
                    </Typography>
                    <Typography sx={{ fontSize: "0.95rem", color: "rgba(255,255,255,0.75)" }}>
                      {call.program_name}
                    </Typography>
                  </Box>
                  {call.budget_display && (
                    <Typography sx={{ fontSize: "1.2rem", fontWeight: 700, color: "rgba(162,130,234,0.95)",
                      fontVariantNumeric: "tabular-nums", letterSpacing: "-0.3px" }}>
                      {call.budget_display}
                    </Typography>
                  )}
                </Box>
              </Box>
              );
            })}
          </Box>

          {/* Agency pills */}
          {/* <Box>
            <Typography sx={{ fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "1.5px",
              color: "rgba(255,255,255,0.3)", mb: 3 }}>
              Funding agencies tracked
            </Typography>
            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
              {["European Commission", "ERC", "Marie Skłodowska-Curie", "NIH", "NSF",
                "Wellcome Trust", "DFG", "ANR", "UKRI", "NWO", "FWF", "Horizon Europe"].map((name) => (
                <Box key={name} sx={{
                  px: 2, py: 0.75,
                  borderRadius: 99,
                  border: "1px solid rgba(255,255,255,0.1)",
                  bgcolor: "rgba(255,255,255,0.04)",
                }}>
                  <Typography sx={{ fontSize: "0.8rem", color: "rgba(255,255,255,0.55)", fontWeight: 500 }}>
                    {name}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box> */}

        </Container>
      </Box>

      {/* ─── FOOTER ─── */}
      <Box
        component="footer"
        sx={{
          bgcolor: "#070810",
          borderTop: "1px solid rgba(255,255,255,0.06)",
          py: 4, px: { xs: 3, md: 6 },
          display: "flex", flexDirection: { xs: "column", sm: "row" },
          alignItems: { sm: "center" }, justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Typography sx={{ fontSize: "1.1rem", color: "rgba(255,255,255,0.55)" }}>
          © 2026 Helixome Life Sciences and Information Technologies Ltd. All rights reserved.
        </Typography>
        <Box sx={{ display: "flex", gap: 3 }}>
          {["Privacy Policy", "Terms of Use", "Contact"].map((label) => (
            <Typography key={label} component="a" href="/login/"
              sx={{ fontSize: "1.1rem", color: "rgba(255,255,255,0.55)", textDecoration: "none",
                "&:hover": { color: "rgba(255,255,255,0.7)" } }}>
              {label}
            </Typography>
          ))}
        </Box>
      </Box>

    </Box>
  );
}
